import { useCallback } from 'react';
import { type PlanFeatureInput } from '@frameio/graphql-client/generated';
import { useProjectPermissions } from '@frameio/permissions';
import { FeatureGate } from '@frameio/plan-management/feature-gate/components';
import { useFeatureGatePlanSuggestion } from '@frameio/plan-management/feature-gate/hooks';
import { useFeatureGateDisableContext } from '@frameio/plan-management/feature-gate/hooks/useFeatureGateDisableContext';
import { RestrictedAccessToggle } from '@frameio/shared/components';
import { useDebounceCallback } from '@frameio/vapor';
import { useUpdateProjectPrivacyMutation } from '../../gql/generated/UpdateProjectPrivacy';
import { useResourcesGrantingAccess } from '../InviteAccessMembers/hooks/useResourcesGrantingAccess';
import TEXT from './generated/TEXT/ProjectAccessToggle';

TEXT.DEFINE({
  /** Title for the project access toggle */
  title: 'Make Restricted',
  /** Description for the project access toggle */
  description:
    'Only people directly invited to the project will have access, plus admins.',
  /** Description for the project access toggle when it is enabled */
  enabledDescription:
    'Only people directly invited to the project can access, plus admins.',
  /** Error message for the project access toggle */
  error: 'Something went wrong',
});

const DEBOUNCE = 100;
const RESTRICTED_PROJECTS_FEATURE_INPUT: PlanFeatureInput = 'PRIVATE_PROJECTS';

type ProjectAccessToggleProps = {
  projectId: string;
  isProjectPrivate: boolean;
};

export const ProjectAccessToggle = ({
  projectId,
  isProjectPrivate,
}: ProjectAccessToggleProps) => {
  return (
    <FeatureGate
      feature={RESTRICTED_PROJECTS_FEATURE_INPUT}
      placement="top"
      shouldShowTooltip={true}
    >
      <ToggleContainer
        isProjectPrivate={isProjectPrivate}
        projectId={projectId}
      />
    </FeatureGate>
  );
};

export const ToggleContainer = ({
  projectId,
  isProjectPrivate,
}: ProjectAccessToggleProps) => {
  const canEditUserPermissions = useProjectPermissions({
    check: 'canEditUserPermissions',
    projectId,
  });
  const { refetchResources } = useResourcesGrantingAccess(projectId, 'Project');

  const featureGatePlanSuggestion = useFeatureGatePlanSuggestion(
    RESTRICTED_PROJECTS_FEATURE_INPUT
  );
  const shouldUseFeatureGateButton =
    !featureGatePlanSuggestion?.includedInCurrentPlan;

  const featureGateDisableContext = useFeatureGateDisableContext();
  const isFeatureGateActive =
    featureGateDisableContext?.isUpgradeModalButtonActive ?? false;
  const [updateProjectPrivacy] = useUpdateProjectPrivacyMutation();

  const handleUpdateProjectPrivacy = useCallback(
    (isPrivate?: boolean) => {
      if (isPrivate === undefined) return;

      updateProjectPrivacy({
        variables: {
          input: {
            id: projectId,
            private: isPrivate,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateProject: {
            __typename: 'UpdateProjectPayload',
            project: {
              __typename: 'Project',
              id: projectId,
              private: isPrivate,
            },
          },
        },
        onCompleted: () => {
          // Refetch resources data after privacy toggle since restricted projects
          // don't initially load the full access-granting resources data
          if (!isPrivate) {
            refetchResources();
          }
        },
      });
    },
    [projectId, refetchResources, updateProjectPrivacy]
  );

  const [debouncedOnChange] = useDebounceCallback(
    handleUpdateProjectPrivacy,
    DEBOUNCE,
    { leading: true }
  );

  const handleDebouncedOnChange = useCallback(() => {
    debouncedOnChange(!isProjectPrivate);
  }, [debouncedOnChange, isProjectPrivate]);

  return (
    <RestrictedAccessToggle
      canManageAccess={Boolean(canEditUserPermissions)}
      description={
        isProjectPrivate ? TEXT.enabledDescription : TEXT.description
      }
      isFeatureGateActive={isFeatureGateActive}
      isFeatureGated={shouldUseFeatureGateButton}
      isRestricted={isProjectPrivate}
      onChange={handleDebouncedOnChange}
      title={TEXT.title}
    />
  );
};
